import React, { useEffect, useState } from 'react'
import { useSatsueiGuide } from '../hooks'
import { useApiError } from '../../shared/hooks'
import Button from '../../shared/ui/Button'
import MessageDialog from '../../shared/ui/MessageDialog'
import { satsueiGuideService } from '../services/satsueiGuideService'
import { MAX_UPLOAD_FILE_SIZE_BYTES, UPLOAD_FILE_SIZE_ERROR_MESSAGE } from '../constants/upload'
import Layout from '../components/Layout'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  rectIntersection,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// Sortable Guide Item Component using @dnd-kit
const SortableGuideItem = ({ guide, isSelected, onSelect, onNameChange, onSetsumeiChange, onDelete, activeId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: guide.satsueiGuideId })

  const isActive = activeId === guide.satsueiGuideId

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition, // Disable transitions during drag for better performance
    opacity: isActive ? 0.3 : (isDragging ? 0.5 : 1), // Dim original item when active, show ghost when dragging
    maxWidth: '100%', // Prevent horizontal overflow during drag
    boxSizing: 'border-box', // Ensure padding is included in width calculation
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-4 bg-white border rounded-md cursor-move transition-all w-full max-w-full ${
        isSelected
          ? 'border-red-500 bg-red-50'
          : 'border-gray-200'
      }`}
      onClick={() => onSelect(guide.satsueiGuideId)}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <input
            id={`guide-name-${guide.satsueiGuideId}`}
            type="text"
            value={guide.satsueiGuideName}
            onChange={(e) => onNameChange(guide.satsueiGuideId, e.target.value)}
            className="w-full text-lg font-bold border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
            onClick={(e) => e.stopPropagation()}
          />
          <textarea
            id={`guide-description-${guide.satsueiGuideId}`}
            value={guide.setsumei}
            onChange={(e) => onSetsumeiChange(guide.satsueiGuideId, e.target.value)}
            className="w-full mt-2 text-sm border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 resize-none"
            rows="2"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <div className="flex items-center space-x-2 ml-4">
          <Button
            variant="secondary"
            size="md"
            onClick={(e) => {
              e.stopPropagation()
              onSelect(guide.satsueiGuideId)
            }}
          >
            ガイド<br />画像設定
          </Button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete(guide.satsueiGuideId)
            }}
            className="px-2 py-1 bg-red-500 rounded-md flex items-center justify-center text-white text-xs"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  )
}

// Sortable Guide Detail Item Component using @dnd-kit
const SortableGuideDetailItem = ({ detail, index, selectedCategoryId, onImageUpload, onNameChange, onSetsumeiChange, onDelete, activeId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: detail.satsueiGuideDetailId })

  const isActive = activeId === detail.satsueiGuideDetailId

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition, // Disable transitions during drag for better performance
    opacity: isActive ? 0.3 : (isDragging ? 0.5 : 1), // Dim original item when active, show ghost when dragging
    maxWidth: '100%', // Prevent horizontal overflow during drag
    boxSizing: 'border-box', // Ensure padding is included in width calculation
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative p-4 border rounded-lg cursor-move transition-all w-full max-w-full"
      {...attributes}
      {...listeners}
    >

      {/* Image Area */}
      <ImageArea
        detail={detail}
        selectedCategoryId={selectedCategoryId}
        onImageUpload={onImageUpload}
      />

      {/* Number Badge */}
      <div className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
        {index + 1}
      </div>

      {/* Detail Info */}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <input
            id={`detail-name-${detail.satsueiGuideDetailId}`}
            type="text"
            value={detail.satsueiGuideDetailName}
            onChange={(e) => onNameChange(detail.satsueiGuideDetailId, e.target.value)}
            placeholder="ガイド画像名"
            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete(detail.satsueiGuideDetailId)
            }}
            className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0"
          >
            ×
          </button>
        </div>
        <textarea
          id={`detail-description-${detail.satsueiGuideDetailId}`}
          value={detail.setsumei}
          onChange={(e) => onSetsumeiChange(detail.satsueiGuideDetailId, e.target.value)}
          placeholder="説明"
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows="2"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  )
}

// Image Area Component with async image loading
const ImageArea = ({ detail, selectedCategoryId, onImageUpload }) => {
    const [imageUrl, setImageUrl] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        const loadImage = async () => {
            if (detail.filenameExtension && selectedCategoryId !== undefined && selectedCategoryId !== null) {
                setIsLoading(true)
                try {
                    const url = await satsueiGuideService.getImage(
                        selectedCategoryId,
                        detail.satsueiGuideId,
                        detail.satsueiGuideDetailId,
                        detail.filenameExtension
                    )
                    setImageUrl(url)
                } catch (error) {
                    console.error('Failed to load image:', error)
                    setImageUrl(null)
                } finally {
                    setIsLoading(false)
                }
            } else {
                setImageUrl(null)
            }
        }
    
        loadImage()
    }, [detail.satsueiGuideId, detail.satsueiGuideDetailId, detail.filenameExtension, selectedCategoryId])

    return (
        <div
            className="w-full h-40 bg-gray-200 rounded-lg mb-3 flex items-center justify-center relative"
            onDragOver={(e) => {
                e.preventDefault()
                e.currentTarget.classList.add('border-2', 'border-blue-500')
            }}
            onDragLeave={(e) => {
                e.preventDefault()
                e.currentTarget.classList.remove('border-2', 'border-blue-500')
            }}
            onDrop={(e) => {
                e.preventDefault()
                e.currentTarget.classList.remove('border-2', 'border-blue-500')
                const files = e.dataTransfer.files
                if (files.length > 0) {
                    onImageUpload(detail.satsueiGuideId, detail.satsueiGuideDetailId, files[0])
                }
            }}
        >
            {isLoading ? (
                <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                    <div className="text-gray-500">読み込み中...</div>
                </div>
            ) : imageUrl ? (
                <img
                    src={imageUrl}
                    alt={detail.satsueiGuideDetailName}
                    className="max-w-full max-h-full object-contain"
                    onError={() => setImageUrl(null)}
                />
            ) : (
                <div className="text-gray-500 text-center">
                    <div className="text-2xl mb-2">📷</div>
                    <div className="text-sm">画像をドラッグ&ドロップ</div>
                </div>
            )}

            {/* File Input Overlay */}
            <input
                id={`image-upload-${detail.satsueiGuideDetailId}`}
                type="file"
                accept="image/*"
                onChange={(e) => {
                    const file = e.target.files[0]
                    if (file) {
                        onImageUpload(detail.satsueiGuideId, detail.satsueiGuideDetailId, file)
                    }
                }}
                className="absolute inset-0 opacity-0 cursor-pointer"
            />
        </div>
    )
}

const SatsueiGuide = () => {
    const {
        categories,
        selectedCategoryId,
        selectedCategory,
        guides,
        deletedGuides,
        selectedGuideId,
        guideDetails,
        isLoading,
        isUploading,
        setSelectedCategoryId,
        setSelectedGuideId,
        loadCategories,
        loadGuides,
        loadGuideDetails,
        addGuide,
        updateGuide,
        deleteGuide,
        restoreGuide,
        addGuideDetail,
        updateGuideDetail,
        deleteGuideDetail,
        uploadImage,
        saveAll,
        reorderGuides,
        reorderGuideDetails
    } = useSatsueiGuide()

    const { handleApiError } = useApiError()

    // Modal states
    const [showAddGuideModal, setShowAddGuideModal] = useState(false)
    const [showDeletedGuidesModal, setShowDeletedGuidesModal] = useState(false)
    const [showMessageDialog, setShowMessageDialog] = useState(false)
    const [messageDialogContent, setMessageDialogContent] = useState({ title: '', message: '' })

    // Add guide form state
    const [newGuideName, setNewGuideName] = useState('')
    const [newGuideSetsumei, setNewGuideSetsumei] = useState('')

    // Drag state for overlay
    const [activeId, setActiveId] = useState(null)

    // @dnd-kit sensors with activation constraint to prevent accidental drags
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // Reduced from 8px for more responsive dragging
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    // Handle drag start
    const handleDragStart = (event) => {
        setActiveId(event.active.id)
    }

    // Handle drag end for guides reordering
    const handleGuideDragEnd = (event) => {
        const { active, over } = event
        setActiveId(null)

        if (active.id !== over?.id && over) {
            const oldIndex = guides.findIndex(item => item.satsueiGuideId === active.id)
            const newIndex = guides.findIndex(item => item.satsueiGuideId === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(guides, oldIndex, newIndex)
                reorderGuides(newOrder)
            }
        }
    }

    // Handle drag end for guide details reordering
    const handleDetailDragEnd = (event) => {
        const { active, over } = event
        setActiveId(null)

        if (active.id !== over?.id && over) {
            const oldIndex = guideDetails.findIndex(item => item.satsueiGuideDetailId === active.id)
            const newIndex = guideDetails.findIndex(item => item.satsueiGuideDetailId === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(guideDetails, oldIndex, newIndex)
                reorderGuideDetails(newOrder)
            }
        }
    }

    // Initialize data
    useEffect(() => {
        const initializeData = async () => {
            try {
                await loadCategories()
            } catch (error) {
                handleApiError(error, 'カテゴリーの読み込みに失敗しました。')
            }
        }
        initializeData()
    }, [handleApiError, loadCategories])

    // Load guides when category changes
    useEffect(() => {
        if (selectedCategoryId !== undefined && selectedCategoryId !== null) {
            loadGuides(selectedCategoryId)
            setSelectedGuideId(0)
        } else {
            setSelectedGuideId(0)
        }
    }, [selectedCategoryId, loadGuides, setSelectedGuideId])

    // Load guide details when guide is selected
    useEffect(() => {
        if (selectedCategoryId !== undefined && selectedCategoryId !== null && selectedGuideId) {
            loadGuideDetails(selectedCategoryId, selectedGuideId)
        }
    }, [selectedCategoryId, selectedGuideId, loadGuideDetails])

    const handleCategoryChange = (e) => {
        setSelectedCategoryId(e.target.value)
    }

    const handleGuideSelect = (guideId) => {
        setSelectedGuideId(guideId)
    }

    const handleAddGuide = () => {
        if (!newGuideName.trim()) {
            setMessageDialogContent({
                title: 'エラー',
                message: '撮影ガイド名を入力してください。'
            })
            setShowMessageDialog(true)
            return
        }

        try {
            addGuide(newGuideName, newGuideSetsumei)
            setNewGuideName('')
            setNewGuideSetsumei('')
            setShowAddGuideModal(false)
        } catch (error) {
            setMessageDialogContent({
                title: 'エラー',
                message: error.message
            })
            setShowMessageDialog(true)
        }
    }

    const handleModalKeyDown = (e) => {
        if (e.key === 'Escape') {
            setShowAddGuideModal(false)
            setShowDeletedGuidesModal(false)
        }
    }

    const handleDeleteGuide = (guideId) => {
        deleteGuide(guideId)
        if (selectedGuideId === guideId) {
            setSelectedGuideId(0)
        }
    }

    const handleRestoreGuide = (guideId) => {
        restoreGuide(guideId)
        setShowDeletedGuidesModal(false)
    }

    const handleAddGuideDetail = () => {
        if (!selectedGuideId) {
            setMessageDialogContent({
                title: 'エラー',
                message: '先にガイドを選択してください。'
            })
            setShowMessageDialog(true)
            return
        }

        try {
            addGuideDetail()
        } catch (error) {
            setMessageDialogContent({
                title: 'エラー',
                message: error.message
            })
            setShowMessageDialog(true)
        }
    }

    const handleDeleteGuideDetail = (detailId) => {
        deleteGuideDetail(detailId)
    }

    const handleImageUpload = async (guideId, detailId, file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            setMessageDialogContent({
                title: 'エラー',
                message: '画像ファイルを選択してください。'
            })
            setShowMessageDialog(true)
            return
        }

        // Validate file size
        if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
            setMessageDialogContent({
                title: 'エラー',
                message: `${UPLOAD_FILE_SIZE_ERROR_MESSAGE}。`
            })
            setShowMessageDialog(true)
            return
        }

        try {
            await uploadImage(guideId, detailId, file)
        } catch (error) {
            handleApiError(error, '画像のアップロードに失敗しました。')
        }
    }

    const handleSave = async () => {
        try {
            await saveAll()
            setMessageDialogContent({
                title: 'メッセージ',
                message: '登録が完了しました。'
            })
            setShowMessageDialog(true)
        } catch (error) {
            handleApiError(error, '登録に失敗しました。')
        }
    }

    const handleGuideNameChange = (guideId, value) => {
        const guide = guides.find(g => g.satsueiGuideId === guideId)
        if (guide) {
            updateGuide(guideId, value, guide.setsumei)
        }
    }

    const handleGuideSetsumeiChange = (guideId, value) => {
        const guide = guides.find(g => g.satsueiGuideId === guideId)
        if (guide) {
            updateGuide(guideId, guide.satsueiGuideName, value)
        }
    }

    const handleDetailNameChange = (detailId, value) => {
        const detail = guideDetails.find(d => d.satsueiGuideDetailId === detailId)
        if (detail) {
            updateGuideDetail(detailId, value, detail.setsumei)
        }
    }

    const handleDetailSetsumeiChange = (detailId, value) => {
        const detail = guideDetails.find(d => d.satsueiGuideDetailId === detailId)
        if (detail) {
            updateGuideDetail(detailId, detail.satsueiGuideDetailName, value)
        }
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <Layout pageTitle="撮影ガイド管理">

                {/* Main Content */}
                <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6 overflow-x-hidden">
                    <div className="flex flex-col md:flex-row gap-6">
                        {/* Left Panel */}
                        <div className="w-full md:w-96 flex-shrink-0">
                            <div className="bg-white rounded-lg shadow p-6 space-y-6">
                                {/* Category Selection */}
                                <div>
                                    <label htmlFor="category-select" className="block text-sm font-medium text-gray-700 mb-2">
                                        撮影ラベルカテゴリー
                                    </label>
                                    <select
                                        id="category-select"
                                        value={selectedCategoryId}
                                        onChange={handleCategoryChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={isLoading}
                                    >
                                        {/* <option value="0">未指定</option> */}
                                        {categories.map(category => (
                                            <option key={category.satsueiMemoCategoryId} value={category.satsueiMemoCategoryId}>
                                                {category.satsueiMemoCategoryName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Add Guide Button */}
                                <Button
                                    onClick={() => setShowAddGuideModal(true)}
                                    disabled={selectedCategoryId === undefined || selectedCategoryId === null}
                                    className="w-full"
                                >
                                    の撮影ガイドを追加
                                </Button>

                                {/* Guides List */}
                                <div className="space-y-2">
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={rectIntersection}
                                        onDragStart={handleDragStart}
                                        onDragEnd={handleGuideDragEnd}
                                    >
                                        <SortableContext
                                            items={guides.map(guide => guide.satsueiGuideId)}
                                        >
                                            <div className="max-h-96 overflow-y-auto overflow-x-hidden border border-gray-200 rounded-md bg-gray-50 space-y-2">
                                                {guides.map((guide) => (
                                                    <SortableGuideItem
                                                        key={guide.satsueiGuideId}
                                                        guide={guide}
                                                        isSelected={selectedGuideId === guide.satsueiGuideId}
                                                        onSelect={handleGuideSelect}
                                                        onNameChange={handleGuideNameChange}
                                                        onSetsumeiChange={handleGuideSetsumeiChange}
                                                        onDelete={handleDeleteGuide}
                                                        activeId={activeId}
                                                    />
                                                ))}
                                            </div>
                                        </SortableContext>
                                        <DragOverlay>
                                            {activeId ? (
                                                <div className="p-4 bg-white border border-red-500 rounded-md shadow-lg opacity-90 w-full max-w-full">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex-1">
                                                            <div className="w-full text-lg font-bold border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1">
                                                                {guides.find(g => g.satsueiGuideId === activeId)?.satsueiGuideName || 'Dragging...'}
                                                            </div>
                                                            <div className="w-full mt-2 text-sm border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 resize-none">
                                                                {guides.find(g => g.satsueiGuideId === activeId)?.setsumei || ''}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center space-x-2 ml-4">
                                                            <div className="px-2 py-1 bg-gray-200 rounded-md flex items-center justify-center text-xs">
                                                                ガイド<br />画像設定
                                                            </div>
                                                            <div className="px-2 py-1 bg-red-500 rounded-md flex items-center justify-center text-white text-xs">
                                                                ×
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </DragOverlay>
                                    </DndContext>

                                    {/* Deleted Guides Button */}
                                    {deletedGuides.length > 0 && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => setShowDeletedGuidesModal(true)}
                                            className="w-full"
                                        >
                                            削除済ガイド ({deletedGuides.length})
                                        </Button>
                                    )}

                                    {/* Save Button */}
                                    <Button
                                        variant="danger"
                                        onClick={handleSave}
                                        loading={isLoading}
                                        className="w-full text-lg font-bold"
                                    >
                                        登録する
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Right Panel */}
                        <div className="flex-1 min-w-0">
                            <div className="bg-white rounded-lg shadow p-6 overflow-x-hidden">
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={rectIntersection}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDetailDragEnd}
                                >
                                    <SortableContext
                                        items={guideDetails.map(detail => detail.satsueiGuideDetailId)}
                                    >
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-x-hidden">
                                            {guideDetails.map((detail, index) => (
                                                <SortableGuideDetailItem
                                                    key={detail.satsueiGuideDetailId}
                                                    detail={detail}
                                                    index={index}
                                                    selectedCategoryId={selectedCategoryId}
                                                    onImageUpload={handleImageUpload}
                                                    onNameChange={handleDetailNameChange}
                                                    onSetsumeiChange={handleDetailSetsumeiChange}
                                                    onDelete={handleDeleteGuideDetail}
                                                    activeId={activeId}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                    <DragOverlay>
                                        {activeId ? (
                                            <div className="relative p-4 border border-red-500 rounded-lg shadow-lg opacity-90 w-full max-w-full">

                                                {/* Image Area Placeholder */}
                                                <div className="w-full h-40 bg-gray-200 rounded-lg mb-3 flex items-center justify-center relative">
                                                    <div className="text-gray-500 text-center">
                                                        <div className="text-2xl mb-2">📷</div>
                                                        <div className="text-sm">画像をドラッグ&ドロップ</div>
                                                    </div>
                                                </div>

                                                {/* Number Badge */}
                                                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                                                    {guideDetails.findIndex(d => d.satsueiGuideDetailId === activeId) + 1}
                                                </div>

                                                {/* Detail Info */}
                                                <div className="space-y-2">
                                                    <div className="flex items-center space-x-2">
                                                        <div className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500">
                                                            {guideDetails.find(d => d.satsueiGuideDetailId === activeId)?.satsueiGuideDetailName || 'ガイド画像名'}
                                                        </div>
                                                        <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">
                                                            ×
                                                        </div>
                                                    </div>
                                                    <div className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none">
                                                        {guideDetails.find(d => d.satsueiGuideDetailId === activeId)?.setsumei || '説明'}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : null}
                                    </DragOverlay>
                                </DndContext>

                                {/* Add Guide Detail Button */}
                                <div className="mt-6">
                                    <Button
                                        onClick={handleAddGuideDetail}
                                        disabled={!selectedGuideId}
                                        className="w-full"
                                    >
                                        ガイド画像追加
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Guide Modal */}
                {showAddGuideModal && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        onKeyDown={handleModalKeyDown}
                        tabIndex={-1}
                    >
                        <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
                            <h3 className="text-lg font-semibold mb-4">
                                撮影ラベルカテゴリー「{selectedCategory?.satsueiMemoCategoryName}」の撮影ガイドを追加します。
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="new-guide-name" className="block text-sm font-medium text-gray-700 mb-1">
                                        撮影ガイド
                                    </label>
                                    <input
                                        id="new-guide-name"
                                        type="text"
                                        value={newGuideName}
                                        onChange={(e) => setNewGuideName(e.target.value)}
                                        placeholder="撮影時ガイド名"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="new-guide-description" className="block text-sm font-medium text-gray-700 mb-1">
                                        説明
                                    </label>
                                    <textarea
                                        id="new-guide-description"
                                        value={newGuideSetsumei}
                                        onChange={(e) => setNewGuideSetsumei(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                        rows="3"
                                    />
                                </div>
                            </div>
                            <div className="flex space-x-3 mt-6">
                                <Button
                                    onClick={handleAddGuide}
                                    className="flex-1"
                                >
                                    追加
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={() => setShowAddGuideModal(false)}
                                    className="flex-1"
                                >
                                    キャンセル
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Deleted Guides Modal */}
                {showDeletedGuidesModal && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        onKeyDown={handleModalKeyDown}
                        tabIndex={-1}
                    >
                        <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4 max-h-96 overflow-y-auto">
                            <h3 className="text-lg font-semibold mb-4">削除済み撮影ガイド</h3>
                            <div className="space-y-2">
                                {deletedGuides.map(guide => (
                                    <div key={guide.satsueiGuideId} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                        <span className="font-medium">{guide.satsueiGuideName}</span>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleRestoreGuide(guide.satsueiGuideId)}
                                        >
                                            戻す
                                        </Button>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6">
                                <Button
                                    onClick={() => setShowDeletedGuidesModal(false)}
                                    className="w-full"
                                >
                                    閉じる
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Message Dialog */}
                <MessageDialog
                    isOpen={showMessageDialog}
                    onClose={() => setShowMessageDialog(false)}
                    title={messageDialogContent.title}
                    message={messageDialogContent.message}
                />

                {/* Loading Overlay */}
                {isLoading && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                            <p className="mt-2 text-center">読み込み中...</p>
                        </div>
                    </div>
                )}

                {/* Upload Loading Overlay */}
                {isUploading && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                            <p className="mt-2 text-center">アップロード中...</p>
                        </div>
                    </div>
                )}
            </Layout>
        </div>
    )
}

export default SatsueiGuide
